import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Card, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import BedManagementCard from "./BedManagementCard";
import { currentUrl, unsavedChanges } from "../../../App";
import { fetchBeds, fetchRooms, postBed } from "../../../services/httpClient";
import PageTitle from "../../ui/PageTitle";
import Input from "../../ui/Input";
import RoomManagement from "./RoomManagement";
import SelectObject from "../../ui/SelectObject";
import useApp from "../../../utils/useApp";

interface BedManagementState {
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  getData: boolean;
}

const OrgBedManagement = ({ setOrgToSave, setShowToast, setToastText, setToastBody, getData }: BedManagementState) => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { showMenu, auth }: any = useApp();
  const [errMsg, setErrMsg] = useState("");
  const [filterRoom, setFilterRoom] = useState<string>("");
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [newRoom, setNewRoom] = useState<string>("");
  const [newBed, setNewBed] = useState<string>("");
  const [showAddBed, setShowAddBed] = useState(false);
  const [showRooms, setShowRooms] = useState(false);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      getBeds(true);
    }
  }, [getData]);

  useEffect(() => {
    if (auth?.profile.includeHMIS === 1) {
      getBeds(false);
    }
  }, [auth?.profile]);

  const getBeds = (maintainFilter: boolean) => {
    fetchBeds()
      .then((res: any) => {
        const bedList = res[0].data;
        setBeds(bedList);
        if (maintainFilter && filterRoom !== "0" && filterRoom !== "") {
          const roomId = rooms.find((room: any) => room.key === filterRoom)?.id;
          const filtered = bedList.filter((bed: any) => bed.room_id === roomId);
          filtered.sort((a: any, b: any) => {
            return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
          });
          setFilteredBeds(filtered);
        } else {
          bedList.sort((a: any, b: any) => {
            setFilterRoom("0");
            // group by room name
            if (a.room_name !== b.room_name) {
              return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
            }
            // sort beds in each room alphanumerically
            return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
          });
          setBeds(bedList);
          setFilteredBeds(bedList);
        }
        getRooms(bedList);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
        setErrMsg("");
      });
  };

  const getRooms = (bedList: any) => {
    fetchRooms()
      .then((res: any) => {
        let roomList = res[0].data;
        roomList = roomList.map((room: any) => {
          const bedCount = bedList.filter((bed: any) => bed.room_id === room.id).length;
          return {
            ...room,
            name: `${room.name} (${bedCount} beds)`,
          };
        });

        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setRooms(roomList);

        if (roomList.length === 1) {
          setFilterRoom(roomList[0].key);
          const roomId = roomList[0].id;
          const filtered = bedList.filter((bed: any) => bed.room_id === roomId);
          setFilteredBeds(filtered);
        }

        if (roomList.length === 0) {
          setShowRooms(true);
        }
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const filterByRoom = (e: any) => {
    setErrMsg("");
    setFilterRoom(e.target.value);
    setShowAddBed(false);
    if (e.target.value === "0") {
      beds.sort((a: any, b: any) => {
        // group by room name
        if (a.room_name !== b.room_name) {
          return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
        }
        // sort beds in each room alphanumerically
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
      });
      setFilteredBeds(beds);
    } else {
      const roomId = rooms.find((room: any) => room.key === e.target.value)?.id;
      const filtered = beds.filter((bed: any) => bed.room_id === roomId);
      filtered.sort((a: any, b: any) => {
        // sort beds in each room alphanumerically
        return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
      });
      setFilteredBeds(filtered);
    }
  };

  const onClickAddBed = () => {
    setShowAddBed(true);
    setNewRoom(filterRoom);
  };

  const updateNewRoom = (e: any) => {
    setNewRoom(e.target.value);
    if (e.target.value !== "0" && newBed !== "") {
      setSaveEnabled(true);
      unsavedChanges.value = true;
      setOrgToSave(true);
    } else {
      setSaveEnabled(false);
    }
  };

  const updateNewBed = (e: any) => {
    setNewBed(e.target.value);
    if (newRoom !== "0" && newRoom !== "" && e.target.value !== "") {
      setSaveEnabled(true);
      unsavedChanges.value = true;
      setOrgToSave(true);
    } else {
      setSaveEnabled(false);
    }
  };

  const onClickSaveBed = (isDone: boolean = false) => {
    setErrMsg("");
    const roomId = rooms.find((room: any) => room.key === newRoom)?.id;

    const bedObject = {
      room_id: roomId,
      name: newBed,
    };

    postBed(bedObject)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Bed Created");
        setToastBody(newBed);
        setNewBed("");
        setSaveEnabled(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        if (isDone) {
          setShowAddBed(false);
          setNewRoom("");
        }
        getBeds(true);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const onClickDoneAddBed = () => {
    setErrMsg("");
    setShowAddBed(false);
    setNewRoom("");
    setNewBed("");
    setSaveEnabled(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const onClickShowRooms = () => {
    setShowRooms(!showRooms);
    setNewRoom("");
    setNewBed("");
    setSaveEnabled(false);
    setShowAddBed(false);
  };

  const getAll = (maintainFilter: any) => {
    getRooms(getBeds(maintainFilter));
  };

  return (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {rooms.length === 0 ? (
            <PageTitle title={"Bed Management"} type="section" />
          ) : (
            <PageTitle
              title={showRooms ? "Rooms Management" : "Bed Management"}
              type="section"
              buttonTitle={showRooms ? "Beds" : "Rooms"}
              onButtonClick={() => onClickShowRooms()}
            />
          )}
          {showRooms === true ? (
            <RoomManagement
              getItems={getAll}
              rooms={rooms}
              setShowRooms={setShowRooms}
              setOrgToSave={setOrgToSave}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
              filterRoom={filterRoom}
            />
          ) : (
            <>
              <Row className={showMenu ? "my-3 sticky-top card-note-menu-open-sticky" : "my-3 sticky-top card-note-sticky"}>
                <Col xs={12} lg={10}>
                  <Card className="bg-light">
                    <Row className="pe-0 mb-3 mt-3 ms-1 me-1">
                      <Col xs={6}>
                        <SelectObject
                          label="Select a Room"
                          value={filterRoom}
                          options={rooms}
                          onUpdate={(e: any) => filterByRoom(e)}
                          initialOption="View All"
                        />
                      </Col>
                      <Col xs={6} className="d-flex justify-content-end align-items-center">
                        <Button
                          className="app-primary-color button"
                          onClick={(e) => onClickAddBed()}
                          disabled={showAddBed || filterRoom === "" || filterRoom === "0"}
                        >
                          Add Bed
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
              {showAddBed === true && (
                <>
                  <Row className="align-items-center my-3">
                    <Col xs={4} xl={3}>
                      <SelectObject
                        label="Room"
                        value={newRoom}
                        options={rooms}
                        onUpdate={(e: any) => updateNewRoom(e)}
                        required={true}
                        disabled={true}
                      />
                    </Col>
                    <Col xs={8} xl={5}>
                      <Input
                        label="Bed Name"
                        type="text"
                        value={newBed}
                        onUpdate={(e: any) => updateNewBed(e)}
                        hasFocus={true}
                        required={true}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} xl={8}>
                      <Stack direction="horizontal" gap={3}>
                        <Button className="app-secondary-color button" onClick={() => onClickSaveBed(false)} disabled={!saveEnabled}>
                          Save and Add Another
                        </Button>
                        <div className="vr" />
                        <Button className="app-secondary-color button" onClick={() => onClickSaveBed(true)} disabled={!saveEnabled}>
                          Save and Close
                        </Button>
                        <div className="vr" />
                        <Button className="app-primary-color button" onClick={() => onClickDoneAddBed()}>
                          Cancel
                        </Button>
                      </Stack>
                    </Col>
                  </Row>
                </>
              )}
              <Row className="mt-3">
                <Col xs={12} lg={10}>
                  {filteredBeds.length === 0 ? (
                    <p className="mt-2">No Beds Created</p>
                  ) : (
                    <>
                      <Stack gap={2}>
                        {filteredBeds?.map((item: any, index: number) => (
                          <Row key={index}>
                            <Col xs={12}>
                              <BedManagementCard
                                item={item}
                                getItems={getBeds}
                                rooms={rooms}
                                showAddBed={showAddBed}
                                setShowAddBed={setShowAddBed}
                                setOrgToSave={setOrgToSave}
                                setErrMessage={setErrMsg}
                                setShowToast={setShowToast}
                                setToastText={setToastText}
                                setToastBody={setToastBody}
                              />
                            </Col>
                          </Row>
                        ))}
                      </Stack>
                    </>
                  )}
                </Col>
              </Row>
            </>
          )}

          <Row className="mb-5"></Row>
        </>
      )}
    </>
  );
};

export default OrgBedManagement;
